/**
 * HomeNavbar — Navbar-ka Bogga Hore
 *
 * Floating pill navbar leh navigation links, theme toggle, iyo mobile menu
 * Nav links data-giisa wuxuu ku jiraa: data/navData.js
 *
 * Waxaad ka bedeli kartaa:
 *  - Nav links-ka (data/navData.js)
 *  - Logo image-ka (line 29)
 *  - CTA button text-ka "Ku Biir" (line 63, line 103)
 *  - Navbar max-width (line 21)
 */
import { useState } from "react";
import { Sun, Moon, Menu, X } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

/* ── Data — navigation links ── */
import { navLinks } from "./data/navData";

export default function HomeNavbar() {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* Smooth scroll to section by id */
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="fixed left-0 right-0 top-0 z-50 flex justify-center px-4 pt-3">
      {/* ── Main navbar bar ── */}
      <nav
        className="relative flex w-full items-center justify-between gap-6 rounded-full bg-card px-4 py-2.5 sm:px-6 border border-border sm:backdrop-blur-md"
        style={{
          maxWidth: 1040,
          boxShadow: "var(--shadow-nav)",
        }}
      >
        {/* ── Left: Logo ── */}
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {/* Logo mark ships with its own navy field, so it's rendered as a
              rounded tile rather than a bare glyph. */}
          <img
            src={`${process.env.PUBLIC_URL}/images/logos/logo-mark.png`}
            alt="GanacsiHub"
            className="h-7 w-7 sm:h-9 sm:w-9 rounded-[10px] object-cover"
          />
          <span
            className="text-base sm:text-lg font-bold font-heading whitespace-nowrap"
            style={{ letterSpacing: "-0.02em" }}
          >
            <span className="text-navy">Ganacsi</span>
            <span className="text-teal">Hub</span>
          </span>
        </button>

        {/* ── Desktop nav links — quiet weight so the logo and CTA lead ── */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.id)}
              className="whitespace-nowrap text-sm font-medium transition-colors duration-200 hover:text-accent"
              style={{ color: "var(--text-body)" }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* ── Right side: theme toggle + Login + Hamburger (mobile) ── */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-accent-light"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="h-[22px] w-[22px] text-navy" />
            ) : (
              <Moon className="h-[22px] w-[22px] text-navy" />
            )}
          </button>
          {/* Login CTA */}
          <a
            href="/store/login"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-5 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 inline-block"
            style={{
              background: "var(--cta-gradient)",
              boxShadow: `0 2px 10px var(--cta-shadow)`,
            }}
          >
            Login
          </a>
          {/* Hamburger button — mobile only */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex md:hidden h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-accent-light"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5 text-navy" />
            ) : (
              <Menu className="h-5 w-5 text-navy" />
            )}
          </button>
        </div>
      </nav>

      {/* ── Mobile dropdown menu ── */}
      {mobileMenuOpen && (
        <div
          className="absolute md:hidden top-[68px] left-4 right-4 flex justify-center"
          style={{ zIndex: 49 }}
        >
          <div
            className="w-full rounded-2xl bg-card border border-border px-5 py-4 flex flex-col gap-3 animate-in slide-in-from-top-2 duration-200"
            style={{
              maxWidth: 700,
              boxShadow: "var(--shadow-nav)",
            }}
          >
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.id)}
                className="w-full text-left text-sm font-medium text-navy py-2.5 px-3 rounded-xl transition-all duration-200 hover:bg-accent-light hover:text-accent active:scale-[0.98]"
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
